import type { AppliedFrameResult } from '../janus-engine/transport/applied-frame';

export type TruthCommand = { operation_id: string; encounter_id: string; actor_id: string } & Record<string, unknown>;
export interface TruthPush { type: string; command: TruthCommand; applied_frame: AppliedFrameResult }
declare global {
    interface Window {
        ftPushAppliedTruth?: (push: TruthPush) => void | Promise<void>;
        /** Persisted spell sections use the same reconnect outbox, including encounterless sheets. */
        ftPushSavedSpellPrep?: (command: TruthCommand) => void;
        ftPushCampaignTruth?: (frame: { type: string; operation_id: string; campaign_id: string } & Record<string, unknown>) => void;
        ftTruthSaveFailed?: (command: TruthCommand) => void;
        ftSaveAppliedTruth?: (operationId: string, send: () => Promise<Response>) => Promise<Response>;
        ftTruthTransportCounters?: { sent: number; received: number; nacked: number; expired: number; correctionsReceived: number; bridgeVersionMismatch: number };
    }
}

// Stateless bridge: action bundles never import the socket bundle's state.
export async function saveAppliedTruth(endpoint: string, command: { operation_id: string }, init: RequestInit, registerDelivery = true): Promise<Response> {
    const posted = JSON.parse(String(init.body)) as TruthCommand;
    const send = async (): Promise<Response> => {
        const delays = [150, 500, 1500];
        for (let attempt = 0; ; attempt++) {
            let response: Response | undefined, failure: unknown;
            let timer: ReturnType<typeof setTimeout> | undefined;
            try {
                // Bound even a fetch implementation that never settles on abort.
                response = await Promise.race([fetch(endpoint, { ...init, signal: AbortSignal.timeout(6000) }),
                    new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('truth-save-timeout')), 6000); })]);
            } catch (error) { failure = error; }
            finally { clearTimeout(timer); }
            if (response && response.status < 500) return response;
            const delay = delays[attempt];
            if (delay === undefined) {
                window.ftTruthSaveFailed?.(posted);
                if (response) return response;
                throw failure;
            }
            await new Promise(resolve => setTimeout(resolve, delay));
        }
    };
    return registerDelivery ? window.ftSaveAppliedTruth?.(command.operation_id, send) ?? send() : send();
}
